/**
 * surfaceAccess — one role predicate per navigable surface, imported by BOTH the
 * page that enforces it and the rail that offers it.
 *
 * WHY THIS FILE EXISTS (drift found 2026-08-02, hours after the rail role filter
 * shipped). The SPA carried six overlapping "admin" role sets, no two identical:
 *
 *   DashboardPage        core-developer, vertical-product-owner, broker-operator
 *   AdminPage            core-developer, vertical-product-owner
 *   AppOperationsPage    tenant-admin, broker-operator, service-admin, vertical-product-owner
 *   SandboxRequestPage   tenant-admin
 *   catalogOperator      service-admin, broker-operator, vertical-product-owner
 *   useCatalog           tenant-admin, service-admin, broker-operator, vertical-product-owner
 *
 * ruling-2026-08-02-rail-absent-not-denied then wrote the rail's predicates
 * against a SEVENTH notion — isCatalogOperator, which governs the catalogue tile
 * admin row and nothing else. The result was the precise defect that ruling set
 * out to remove, in both directions: the rail offered Dashboard and
 * Administration to a service-admin both pages deny, and hid them from a
 * core-developer both pages admit.
 *
 * The rule this file enforces: A RAIL PREDICATE IS NEVER WRITTEN BY HAND. It is
 * the destination page's own gate, imported. surfaceAccess.test.ts asserts the
 * two agree for every role that exists in the realm.
 *
 * These constants record what each surface ENFORCES TODAY — they are a
 * description, not a proposal. Moving a role between them is a policy change and
 * belongs in the graph first.
 *
 * ── 2026-08-02: both open questions in the previous header are now RESOLVED ──
 *
 * open-task-2026-08-02-broker-operator-role-absent-in-realm → resolved STALE.
 *   broker-operator existed in no realm and as no BrokerRole node; it had never
 *   matched anybody. It is removed from every role set in this SPA. If the role
 *   is ever created it must be added to the vertical-broker Keycloak login gate
 *   in the SAME change, or its holders are locked out of the broker they operate.
 *
 * open-task-2026-08-02-tenant-admin-vs-operator-scope → resolved by
 *   decision-2026-08-02-tenant-admin-scope-is-registry-mutation: policy-2026-08-01
 *   is about registry MUTATION specifically, not about who operates apps. A
 *   tenant-admin never creates, deploys, re-describes or publishes; a tenant-admin
 *   DOES operate the apps of their own tenant set. The defect was never these role
 *   sets — it was one boolean in Tile.tsx conflating mode with role, retired by
 *   decision-2026-08-02-tile-is-a-pointer-manage-is-the-workbench.
 *
 * ── 2026-08-03: THE FIVE-ROLE MODEL (decision-2026-08-03-role-model-planes) ──
 *
 * The fourteen-role sprawl above is retired. Every admin-surface role in this
 * file collapses into ONE name, `broker-admin`, defined separately in EACH
 * broker realm and meaning "administrator of THE BROKER THIS REALM SERVES".
 * The realm is the scope; issuer validation is what keeps a vertical
 * broker-admin out of platform authority. Retired here: `service-admin`,
 * `core-developer`, `vertical-product-owner`, `platform-admin`,
 * `feature-reviewer`, `prospect-viewer` (and `broker-operator`, already gone).
 * `service-developer` (developer capacity), `tenant-admin` and `uat-approver`
 * are unchanged; `user` is renamed `tenant-user`.
 *
 * COEXISTENCE: until WP-5 deletes the old Keycloak roles, a deployment MAY
 * still ACCEPT old names through env on the server side. The DEFAULTS below
 * carry the new model — code is the real control, env is the temporary shim.
 */

import type { InstanceMode } from '../config/schema';

export type HasRole = (role: string) => boolean;

/** Broker-operations dashboard. Platform-side monitoring, not a tenant surface. */
export const DASHBOARD_ROLES: readonly string[] = ['broker-admin'];

/** The /admin group: Tenants, Sub-clients, Orders, Subscriptions, Audit log. */
export const ADMIN_SURFACE_ROLES: readonly string[] = ['broker-admin'];

/** Filing a sandbox request on the shared vertical broker (PR #32). */
export const SANDBOX_REQUEST_ROLES: readonly string[] = ['tenant-admin'];

/**
 * The per-app App Operations Board (adr-075 step 5). The one management surface
 * a tenant-admin is explicitly a first-class user of, not a guest on.
 */
export const APP_OPERATIONS_ROLES: readonly string[] = [
  'tenant-admin',
  'broker-admin',
];

/**
 * Roles whose TENANT SET IS UNBOUNDED — platform-scoped, not tenant-scoped.
 *
 * This is the distinction open-task-2026-08-02-tenant-admin-vs-operator-scope
 * found missing: `tenant-admin` is tenant-scoped ("within the tenant"), while
 * the broker's own administrator is the one genuine platform role.
 * A platform-scoped caller operates every app; a tenant-scoped caller operates
 * the apps of their own tenant set. Nothing here grants a capability — it only
 * decides whether tenant membership BOUNDS a capability the role already has.
 *
 * ⚠ THIS LIST AND infraops services/service-broker/src/lib/board-scope.ts
 * `BOARD_PLATFORM_SCOPED_ROLES` ARE ONE PREDICATE EVALUATED TWICE — the
 * affordance here, the enforcement there. THEY CHANGE TOGETHER, in the same
 * PR, or the board offers what the API refuses (or worse, the reverse).
 * 2026-08-03: both are now `broker-admin` (decision-2026-08-03-role-model-planes).
 */
export const PLATFORM_SCOPED_ROLES: readonly string[] = ['broker-admin'];

const anyOf = (roles: readonly string[]) => (hasRole: HasRole): boolean =>
  roles.some((r) => hasRole(r));

export const canUseDashboard = anyOf(DASHBOARD_ROLES);
export const canUseAdminSurface = anyOf(ADMIN_SURFACE_ROLES);
export const canRequestSandbox = anyOf(SANDBOX_REQUEST_ROLES);
export const canUseAppOperations = anyOf(APP_OPERATIONS_ROLES);

// ── Tenant-set membership ────────────────────────────────────────────────────

/**
 * The caller, as far as tenant scoping is concerned.
 *
 * `company` is the Keycloak claim the rest of the platform already reads as
 * "the tenant": the sandbox-request surface resolves its tenant from `$.company`
 * (SANDBOX_REQUEST_TENANT_CLAIM), and the Hasura claims_map maps
 * x-hasura-company from `$.company` with NO default. Using anything else here
 * would introduce a second, disagreeing answer to the same question.
 */
export interface CallerIdentity {
  /** The verified `company` claim. Never taken from a request body or config. */
  company?: string | null;
  hasRole: HasRole;
}

const norm = (s: string | null | undefined): string => (s ?? '').trim().toLowerCase();

/**
 * Does this caller's tenant set contain this app? THE ONLY PLACE THIS IS ANSWERED.
 *
 * ⚠ IDENTITY GAP, DELIBERATELY NOT FIXED HERE. `company` is SINGLE-VALUED, and
 * the real relation is N:M — one person can administer several tenants, and one
 * tenant has several administrators. v1 therefore models a tenant SET of size
 * one for tenant-scoped roles. Widening it means a multi-valued claim (or a
 * server-side membership lookup) and is a separate change; when it lands, this
 * function is the single call site that has to learn about it.
 *
 * ⚠ SECOND GAP, LIVE TODAY: the registry records no app→tenant relation at all.
 * `service.owner` holds the JWT sub of whoever REGISTERED the app and joins to
 * `tenant.admin_subject` zero times; the only real app↔tenant link is through
 * subscriptions. `service.tenant` is the column that closes this, and it is a
 * schema change (Category A). Until it exists, `appTenant` arrives null for
 * every app and rule 2 below decides.
 *
 * The rules, in order:
 *   1. A PLATFORM-SCOPED caller's tenant set is unbounded → always contained.
 *   2. An app with NO RECORDED TENANT is UNKNOWN, not foreign → contained.
 *      This is decision-2026-08-02-rail-absent-not-denied read the right way
 *      round: an unallocated registry app is not "some other tenant's app", and
 *      refusing here would make the board unreachable for the very role
 *      adr-075 D11 calls its first-class user. The board and every control on
 *      it re-check server-side regardless — this predicate hides, it never gates.
 *   3. A RECORDED tenant must match the caller's. This is the fail-closed leg,
 *      and it becomes the live one the moment `service.tenant` carries data.
 *   4. A tenant-scoped caller with NO company claim, against a recorded tenant,
 *      is refused. A missing value that decides who you are is an error, not a
 *      wildcard.
 */
export function tenantSetContains(
  caller: CallerIdentity,
  appTenant?: string | null,
): boolean {
  if (PLATFORM_SCOPED_ROLES.some((r) => caller.hasRole(r))) return true;  // 1
  const app = norm(appTenant);
  if (!app) return true;                                                   // 2
  const mine = norm(caller.company);
  if (!mine) return false;                                                 // 4
  return mine === app;                                                     // 3
}

/**
 * May this caller open the App Operations Board FOR THIS APP?
 *
 * decision-2026-08-02-tile-is-a-pointer-manage-is-the-workbench: this is the
 * whole of the Manage affordance's predicate — role ∧ membership — and it is
 * imported, never re-written inline at a call site (the 2026-08-02 rail drift
 * is why that rule exists).
 */
export function canManageApp(caller: CallerIdentity, appTenant?: string | null): boolean {
  return canUseAppOperations(caller.hasRole) && tenantSetContains(caller, appTenant);
}

/**
 * Roles that may ORDER an app for a tenant. Ordering commits a tenant to an
 * entitlement and a cost — it is procurement, not consumption, and by
 * decision-2026-08-02-ordering-is-a-tenant-admin-verb it is executed on the
 * VERTICAL BROKER, never from a tenant user's own store.
 */
export const ORDER_ROLES: readonly string[] = ['tenant-admin', 'broker-admin'];

export const canOrder = anyOf(ORDER_ROLES);

/**
 * May this caller ORDER this app FOR ITS TENANT?
 *
 * The two legs the work package states — "tenant-admin for own tenant,
 * broker-admin on behalf" — fall straight out of tenantSetContains and need no
 * second rule here: a `broker-admin` is PLATFORM_SCOPED, so rule 1 makes every
 * tenant contained (ordering ON BEHALF of any); a `tenant-admin` is tenant-scoped,
 * so rules 3/4 bound them to their own `company` (ordering FOR their own tenant).
 * This predicate is therefore role ∧ membership — the exact shape of canManageApp
 * — and like canManageApp it is IMPORTED at the call site, never re-derived inline
 * (the 2026-08-02 rail drift is why that rule exists). It hides an affordance; the
 * server-side ORDER_ROLES gate re-checks regardless.
 */
export function canOrderForTenant(caller: CallerIdentity, appTenant?: string | null): boolean {
  return canOrder(caller.hasRole) && tenantSetContains(caller, appTenant);
}

// ── Surface planes (ADR-075 vocabulary) ──────────────────────────────────────
// planeForMode() lives in shell/routes.ts next to routesForMode, because it is a
// property of the DEPLOYMENT. It is re-exported here ONLY so a reader of this
// file sees that plane and role are two different questions and never one.
export type { SurfacePlane } from '../shell/routes';
export type { InstanceMode };
